import React, { useState } from 'react';
import { useWishlist } from './WishlistContext';
import { useCart } from '../../contexts/CartContext';
import Header from '../index/components/Header/Header';
import './styles/Wishlist.css';
import NavigationMobileMenu from '@/shared/components/NavigationMobileMenu/NavigationMobileMenu';
import useIsMobile from '@/shared/hooks/useIsMobile';
import { toast } from 'react-toastify';
import FlotingCart from '../index/components/Header/floatingcart';

const Wishlist: React.FC = () => {
  const { items, removeItem, loading, navigateToProduct } = useWishlist();
  const { addToCart, getCartItemCount } = useCart();
  const isMobile = useIsMobile();
  const [addingToCart, setAddingToCart] = useState<Set<string>>(new Set());

  const CART_ITEM_LIMIT = 15;
  const totalCartItems = getCartItemCount();

  const handleAddToCart = async (item: any) => {
    try {
      const product = item.product;
      const itemKey = `${product.id}-${item.variantSku || 'default'}`;
      
      // Prevent duplicate operations
      if (addingToCart.has(itemKey)) {
        return;
      }

      setAddingToCart(prev => new Set([...prev, itemKey]));

      // Check cart limit
      const newItemCount = totalCartItems + 1;
      if (newItemCount > CART_ITEM_LIMIT) {
        toast.error(`Cart limit of ${CART_ITEM_LIMIT} items reached`);
        return;
      }

      const variantSku = item.variantSku || product.sku || '';
      const priceToUse = item.variantInfo?.price ?? product.price ?? 0;

      // Extract image properly - handle both object and string formats
      let productImage = '';
      if (product.images && Array.isArray(product.images) && product.images.length > 0) {
        const firstImage = product.images[0];
        productImage = typeof firstImage === 'string' ? firstImage : (firstImage?.url || '');
      }
      if (!productImage && product.imageUrl) {
        productImage = product.imageUrl;
      }
      if (!productImage && product.image) {
        productImage = product.image;
      }

      // Create cart product object
      const cartProduct = {
        id: product.id || product._id,
        _id: product.id || product._id,
        name: product.title || product.name || 'Unnamed Product',
        title: product.title || product.name || 'Unnamed Product',
        price: priceToUse,
        image: productImage,
        imageUrl: productImage,
        slug: product.slug,
        description: product.description || '',
        sku: product.sku || variantSku,
        quantity: 1
      };

      await addToCart(cartProduct, 1, variantSku);
      
      await removeItem(product.id, item.variantSku || '');
    } catch (error) {
    } finally {
      const product = item.product;
      const itemKey = `${product.id}-${item.variantSku || 'default'}`;
      setAddingToCart(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemKey);
        return newSet;
      });
    }
  };

  const handleRemoveItem = (productId: string, variantSku?: string) => {
    removeItem(productId, variantSku || '');
  };


  if (loading) {
    return (
      <>
        <Header />
        <div className="wishlist-page">
          <div className="wishlist-loading">
            <p>Loading your wishlist...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="wishlist-page">
        <div className="wishlist-content">


          {/* Left Section - Wishlist Items */}
          <div className="wishlist-left">
            <div className="wishlist-title-row">
              <h1 className="wishlist-title">Your Wishlist</h1>
          
            </div>

            {items.length === 0 ? (
              <div className="empty-wishlist">
                <div className="empty-wishlist-icon">
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </div>
                <h3>Your wishlist is empty</h3>
                <p>Add items to your cart or continue shopping</p>
              </div>
            ) : (
              <div className="wishlist-table">
                <div className="wishlist-table-header">
                  <div className="header-item">Wishlist Items</div>
                  <div className="header-price">Price</div>
                  <div className="header-actions">Actions</div>
                </div>

                <div className="wishlist-items-list">
                  {items.map((item) => {
                    const product = item.product;
                    let mainImage = (product as any).imageUrl || (product as any).image;
                    if (!mainImage && product.images && Array.isArray(product.images) && product.images.length > 0) {
                      const firstImage = product.images[0];
                      mainImage = typeof firstImage === 'string' ? firstImage : (firstImage?.url || '');
                    }
                    if (!mainImage) {
                      mainImage = "https://via.placeholder.com/300x300?text=No+Image";
                    }
                    const itemKey = `${product.id}-${item.variantSku || 'default'}`;
                    const isAddingToCart = addingToCart.has(itemKey);
                    
                    return (
                      <div key={itemKey} className="wishlist-item-row">
                        <div className="wishlist-item-info">
                          <div className="wishlist-item-image">
                            <img
                              src={mainImage}
                              alt={product.title || 'Product'}
                              onClick={() => navigateToProduct(item)}
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                          <button 
                            className="wishlist-remove-btn" 
                            onClick={() => handleRemoveItem(product.id, item.variantSku)}
                            aria-label="Remove from wishlist"
                            disabled={isAddingToCart}
                          >
                            ✕
                          </button>
                          <div className="wishlist-item-details">
                            <h3 
                              className="item-title"
                              onClick={() => navigateToProduct(item)}
                              style={{ cursor: 'pointer' }}
                            >
                              {product.title || 'Unnamed Product'}
                            </h3>
                            <p className="item-size">One Size</p>
                            <p className="item-sku">SKU: {item.variantSku || product.sku || 'N/A'}</p>
                          </div>
                        </div>

                        <div className="wishlist-item-price">
                          ₹{Math.round(product.price || 0)}
                        </div>

                        <div className="wishlist-item-actions">
                          <button 
                            className="add-to-cart-btn"
                            onClick={() => handleAddToCart(item)}
                            disabled={isAddingToCart}
                            style={{ opacity: isAddingToCart ? 0.6 : 1 }}
                          >
                            {isAddingToCart ? (
                              <>
                                <div style={{ 
                                  width: '16px', 
                                  height: '16px', 
                                  border: '2px solid white', 
                                  borderTopColor: 'transparent',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }} />
                                Adding...
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M1.333 1.333h1.334l.933 5.6a1.333 1.333 0 0 0 1.333 1.067h6.4a1.333 1.333 0 0 0 1.334-1.067l.666-4H4.667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <circle cx="6" cy="13.333" r="1" fill="currentColor"/>
                                  <circle cx="12.667" cy="13.333" r="1" fill="currentColor"/>
                                </svg>
                                Add to Cart
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        <FlotingCart />
      </div>
      {isMobile && <NavigationMobileMenu />}
    </>
  );
};

export default Wishlist;